import { useState } from "react";
import { BAY_ISLE, TERRACOTTA, SEMI_SWEET, PROPER_GREY, RULE, WHITE, FONT_DISPLAY, FONT_DISPLAY_HEAVY, FONT_SERIF, FONT_BODY } from "../theme";
import Testimonials from "../components/Testimonials";
import { TESTIMONIALS } from "../data/testimonials";

const BIZ_REVIEWS = TESTIMONIALS.filter(t => t.audience === "business" || t.audience === "community");

function encode(data) {
  return Object.entries(data).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join("&");
}

export default function ForBusinesses() {
  const [name, setName]           = useState("");
  const [email, setEmail]         = useState("");
  const [business, setBusiness]   = useState("");
  const [industry, setIndustry]   = useState("");
  const [message, setMessage]     = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError]         = useState("");
  const [loading, setLoading]     = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError("Please fill in your name and email."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Please enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: encode({
          "form-name": "acadia-business-inquiry", "bot-field": "",
          full_name: name.trim(), email: email.trim(),
          business_name: business.trim(), industry: industry.trim(),
          message: message.trim(),
        }),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 18px 4rem" }}>

      <div style={{ textAlign: "center", padding: "2.75rem 0 2rem" }}>
        <p style={{
          fontSize: 10, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase",
          color: TERRACOTTA, borderBottom: `1.5px solid ${TERRACOTTA}`,
          display: "inline-block", paddingBottom: 4, marginBottom: 18, fontFamily: FONT_BODY,
        }}>
          For local businesses
        </p>
        <h1 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(26px, 5.5vw, 40px)", color: BAY_ISLE, lineHeight: 1.12, marginBottom: 14, fontWeight: 400 }}>
          AI content for{" "}
          <em style={{ color: TERRACOTTA, fontStyle: "italic", fontFamily: FONT_SERIF, fontSize: "1.15em" }}>local businesses</em>
        </h1>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.75, maxWidth: 480, margin: "0 auto 8px", fontFamily: FONT_BODY }}>
          Social content, captions, reels, and more — AI-produced and tailored to your brand.
          Every engagement is fully custom. Tell us about your business and we'll put together a plan built for you.
        </p>
      </div>

      {submitted ? (
        <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "2.5rem 1rem", textAlign: "center" }}>
          <div style={{
            width: 56, height: 56, borderRadius: "50%", background: "rgba(195,135,98,0.1)",
            display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1.25rem",
          }}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
              <path d="M4 11l4.5 4.5L18 6" stroke={TERRACOTTA} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p style={{ fontFamily: FONT_SERIF, fontSize: 26, color: BAY_ISLE, marginBottom: 8, fontStyle: "italic" }}>
            Message received!
          </p>
          <p style={{ fontSize: 13, color: PROPER_GREY, lineHeight: 1.65, maxWidth: 360, margin: "0 auto 16px", fontFamily: FONT_BODY }}>
            Thanks for reaching out. We'll review your inquiry and get back to you within 1–2 business days with a custom plan.
          </p>
          <div style={{
            display: "inline-block", fontSize: 12, color: PROPER_GREY,
            background: "rgba(33,62,68,0.05)", border: `1px solid ${RULE}`, borderRadius: 8, padding: "10px 18px",
          }}>
            Follow along → <span style={{ color: TERRACOTTA, fontWeight: 700 }}>@acadiamediamarketing</span>
          </div>
        </div>
      ) : (
        <form name="acadia-business-inquiry" method="POST" data-netlify="true" data-netlify-honeypot="bot-field" onSubmit={handleSubmit} noValidate>
          <input type="hidden" name="bot-field" />
          <input type="hidden" name="form-name" value="acadia-business-inquiry" />

          <div style={{ background: WHITE, border: `1px solid ${RULE}`, borderRadius: 14, padding: "24px 20px", marginBottom: "1.25rem" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label>Full name *</label>
                <input type="text" name="full_name" placeholder="Jane Smith" value={name} onChange={e => setName(e.target.value)} autoComplete="name" />
              </div>
              <div>
                <label>Email address *</label>
                <input type="email" name="email" placeholder="jane@yourbusiness.com" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" />
              </div>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label>Business name</label>
                <input type="text" name="business_name" placeholder="Your business name" value={business} onChange={e => setBusiness(e.target.value)} />
              </div>
              <div>
                <label>Industry <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
                <input type="text" name="industry" placeholder="Salon, restaurant, retail..." value={industry} onChange={e => setIndustry(e.target.value)} />
              </div>
            </div>
            <div>
              <label>What are you looking for? <span style={{ fontWeight: 400, color: PROPER_GREY }}>(optional)</span></label>
              <textarea
                name="message"
                placeholder="Tell us what you need — social content, reels, a full content plan, something else entirely..."
                value={message}
                onChange={e => setMessage(e.target.value)}
                rows={4}
              />
            </div>
          </div>

          {error && (
            <p style={{ fontSize: 12, color: "#7a2a2a", background: "rgba(180,60,60,0.06)", border: "1px solid rgba(180,60,60,0.15)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontFamily: FONT_BODY }}>
              {error}
            </p>
          )}

          <button type="submit" disabled={loading} style={{ width: "100%" }}>
            {loading ? "Sending..." : "Get in touch →"}
          </button>

          <p style={{ fontSize: 11, color: PROPER_GREY, textAlign: "center", marginTop: 9, lineHeight: 1.5, fontFamily: FONT_BODY }}>
            No commitment required. We'll put together a custom plan and reach out personally.
          </p>
        </form>
      )}

      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "center", marginTop: "2.25rem", paddingTop: "1.75rem", borderTop: `1px solid ${RULE}` }}>
        {["AI-written content", "Consistent posting", "Built for local businesses", "No experience needed", "Fully custom pricing"].map((t, i) => (
          <span key={i} style={{ fontSize: 11, fontWeight: 500, padding: "5px 11px", borderRadius: 999, background: WHITE, border: `1px solid ${RULE}`, color: PROPER_GREY, fontFamily: FONT_BODY }}>{t}</span>
        ))}
      </div>
    </div>
    <div style={{ background: WHITE, padding: "4rem 20px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: TERRACOTTA, marginBottom: 8, fontFamily: FONT_BODY }}>
          Custom AI Builds
        </p>
        <h2 style={{ fontFamily: FONT_DISPLAY_HEAVY, fontSize: "clamp(22px, 3.5vw, 30px)", color: BAY_ISLE, fontWeight: 400, marginBottom: 12, lineHeight: 1.2 }}>
          Need something built from scratch?
        </h2>
        <p style={{ fontSize: 14, color: SEMI_SWEET, lineHeight: 1.8, maxWidth: 540, marginBottom: "2rem", fontFamily: FONT_BODY }}>
          We build fully custom AI-powered tools, websites, and digital assets for businesses that want something uniquely theirs. Every project is quoted individually.
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: "2.5rem" }}>
          {[
            { icon: "ti-world", label: "AI-Powered Websites", desc: "Custom sites built around your brand, your market, your goals." },
            { icon: "ti-calculator", label: "Interactive Tools & Calculators", desc: "Lead capture tools, pricing estimators, service finders." },
            { icon: "ti-layout-dashboard", label: "Branded Dashboards", desc: "Visual reporting and data displays built for your business." },
            { icon: "ti-file-text", label: "Digital Assets & Artifacts", desc: "Branded PDFs, guides, decks, and templates you can actually use." },
          ].map((item, i) => (
            <div key={i} style={{ background: "rgba(33,62,68,0.03)", border: `1px solid ${RULE}`, borderRadius: 12, padding: "18px 16px" }}>
              <i className={`ti ${item.icon}`} style={{ fontSize: 20, color: TERRACOTTA, display: "block", marginBottom: 10 }} />
              <p style={{ fontSize: 13, fontWeight: 700, color: BAY_ISLE, marginBottom: 6, fontFamily: FONT_DISPLAY }}>{item.label}</p>
              <p style={{ fontSize: 12.5, color: SEMI_SWEET, lineHeight: 1.65, fontFamily: FONT_BODY }}>{item.desc}</p>
            </div>
          ))}
        </div>
        <a href="/get-started" style={{ display: "inline-block", padding: "12px 24px", borderRadius: 9, background: BAY_ISLE, color: WHITE, textDecoration: "none", fontSize: 12, fontWeight: 700, letterSpacing: "0.08em", fontFamily: FONT_BODY }}>
          Request a custom quote →
        </a>
      </div>
    </div>
    <Testimonials reviews={BIZ_REVIEWS} dark={false} label="Client stories" />
    </>
  );
}
